/**
 * Functions and types for working with face groups, which are used for wireframe rendering.
 * @module
 */
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import { faceByThreePoints, type Face } from "geom/brep";
import { buildPolygonsFromFaces } from "geom/bsp";
import type { PolygonBSP } from "geom/bspTypes";
import type { Mesh3AndId } from "geom/mesh";
import type { UVTransformation } from "geom/uv";
import { UVGeneration } from "geom/uv";
import { defaultHatchMaterialFactory } from "./plans/hatchMaterialFactory";

export interface FaceGroupBSP {
  faces: Face[];
  uvGen: UVGeneration;
  materialTransformation?: UVTransformation | UVTransformation[];
  designMaterialId?: MaterialSrc;
  entityId?: string;
  // Reporting-unit attribution used to filter faces to a selected reporting unit at render time.
  // reportingUnitLabelId takes precedence over entityHandleForSelectingReportingUnit when set.
  entityHandleForSelectingReportingUnit?: DocEntityHandle;
  reportingUnitLabelId?: string;
  generateNeighbors?: boolean;
}

export function faceGroupBSPNew(
  faces: Face[],
  designMaterialId: MaterialSrc | null,
  uvGen: UVGeneration,
  materialTransformation?: UVTransformation | UVTransformation[],
  entityId?: string,
  generateNeighbors?: boolean,
  entityHandleForSelectingReportingUnit?: DocEntityHandle,
  reportingUnitLabelId?: string,
): FaceGroupBSP {
  return {
    faces,
    designMaterialId: designMaterialId ?? undefined,
    uvGen,
    materialTransformation,
    entityId,
    generateNeighbors,
    entityHandleForSelectingReportingUnit,
    reportingUnitLabelId,
  };
}

export async function buildPolygonsFromFaceGroups(
  groups: FaceGroupBSP[],
  priority: number,
  isBuildingMirrored: boolean,
  polygons: PolygonBSP[] = [],
): Promise<PolygonBSP[]> {
  for (let groupIndex = 0, groupCount = groups.length; groupIndex < groupCount; groupIndex++) {
    const { faces, designMaterialId, uvGen, entityId, materialTransformation, generateNeighbors } = groups[groupIndex];

    const hatchMaterial = await defaultHatchMaterialFactory.getHatchMaterialForDesignMaterial(designMaterialId);

    let xformMaybeMirrored: UVTransformation | UVTransformation[] | undefined;

    if (materialTransformation) {
      xformMaybeMirrored = Array.isArray(materialTransformation)
        ? materialTransformation.map((xform) => ({
            ...xform,
            isMirrored: isBuildingMirrored,
          }))
        : {
            ...materialTransformation,
            isMirrored: isBuildingMirrored,
          };
    }

    buildPolygonsFromFaces({
      faces,
      priority,
      polygons,
      extendedOptions: {
        designMaterialId,
        hatchMaterial,
        uvGen,
        entityId,
        materialTransformation: xformMaybeMirrored,
        generateNeighbors,
      },
    });
  }

  return polygons;
}

export function buildPolygonsFromFaceGroupsNoMaterials(
  groups: FaceGroupBSP[],
  priority: number,
  polygons: PolygonBSP[] = [],
): PolygonBSP[] {
  for (let groupIndex = 0, groupCount = groups.length; groupIndex < groupCount; groupIndex++) {
    const { faces, designMaterialId, uvGen, entityId } = groups[groupIndex];

    buildPolygonsFromFaces({
      faces,
      priority,
      polygons,
      extendedOptions: {
        designMaterialId,
        uvGen,
        entityId,
      },
    });
  }

  return polygons;
}

interface BuildFaceGroupsFromMeshOptions {
  mesh: Mesh3AndId;
  uvGen?: UVGeneration;
  entityId?: string;
  faceGroups?: FaceGroupBSP[];
}

export function buildFaceGroupsFromMesh({ mesh, faceGroups = [] }: BuildFaceGroupsFromMeshOptions): FaceGroupBSP[] {
  const faces: Face[] = [];
  const { tris, verts } = mesh.mesh;

  for (let i = 0, l = tris.length; i < l; i += 3) {
    const i1 = tris[i];
    const i2 = tris[i + 1];
    const i3 = tris[i + 2];

    const t1 = i1 * 3;
    const t2 = i2 * 3;
    const t3 = i3 * 3;

    const x1 = verts[t1];
    const y1 = verts[t1 + 1];
    const z1 = verts[t1 + 2];

    const x2 = verts[t2];
    const y2 = verts[t2 + 1];
    const z2 = verts[t2 + 2];

    const x3 = verts[t3];
    const y3 = verts[t3 + 1];
    const z3 = verts[t3 + 2];

    const face = faceByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

    if (face) {
      faces.push(face);
    }
  }

  if (faces.length > 0) {
    const faceGroup = faceGroupBSPNew(
      faces,
      null, // designMaterialId - not used
      UVGeneration.NONE,
      undefined, // materialTransformation - not used
      mesh.id,
      false, // generateNeighbors - not used
    );

    faceGroups.push(faceGroup);
  }

  return faceGroups;
}
